import { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import { localeForClientConfigLanguage } from '../../../i18n/types'
import { useFamilyPortalApiClient } from '../../auth/hooks/useFamilyPortalApiClient'
import {
  consentToTestingFromConsentConfig,
  type ConsentConfigPayload,
  type ConsentToTestingEntry,
} from '../consentConfig'
import { CONSENT_TO_TESTING_NOT_APPLICABLE, MULTIPLE_PENDING_ORDERS, readConsentErrorCode } from '../consentErrors'

/**
 * Primary consent copy from `GET /config/consents/primary` → `consentToTesting`.
 * Picks the entry matching the active locale (English/Spanish), with English fallback.
 */
export type ConsentLanguage = {
  primaryConsentParagraphs: string[]
  primaryGuardianCheckboxText: string
  comparatorTitle: string | null
  guardianProbandOnlyTitle: string | null
  guardianDuoTrioTitle: string | null
  /** Multi-subject sentence copy, all from one language: the page's when it has any, else English. */
  guardianSiblingsTitle?: string
  guardianSiblingsAndSelfTitle?: string
  subjectSampleLabel?: string
  ownSampleLabel?: string
  /** Locale the multi-subject copy is written in; the names in the sentence are joined in it. */
  multiSubjectLocale?: string
}

type MultiSubjectCopy = Pick<
  ConsentLanguage,
  'guardianSiblingsTitle' | 'guardianSiblingsAndSelfTitle' | 'subjectSampleLabel' | 'ownSampleLabel'
>

export type ConsentLanguageError =
  | 'not_found'
  | 'upstream_error'
  | 'unavailable'
  | 'invalid_payload'
  | 'missing_content'
  | 'consent_not_applicable'
  | 'multiple_pending_orders'

function nonEmptyString(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined
  const trimmed = raw.trim()
  return trimmed ? trimmed : undefined
}

function nonEmptyStringArray(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const out: string[] = []
  for (const entry of raw) {
    if (typeof entry !== 'string') continue
    const trimmed = entry.trim()
    if (trimmed) out.push(trimmed)
  }
  return out.length > 0 ? out : undefined
}

function nullableNonEmptyString(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  return trimmed ? trimmed : null
}

function englishEntry(payload: ConsentConfigPayload | undefined): ConsentToTestingEntry | undefined {
  const entries = payload?.consentToTesting
  return Array.isArray(entries) ? entries.find((entry) => entry?.language === 'English') : undefined
}

/** The multi-subject fields of one entry, or null when it has none. */
function multiSubjectCopyOf(entry: ConsentToTestingEntry | undefined): MultiSubjectCopy | null {
  const copy = {
    guardianSiblingsTitle: nonEmptyString(entry?.guardianSiblingsTitle),
    guardianSiblingsAndSelfTitle: nonEmptyString(entry?.guardianSiblingsAndSelfTitle),
    subjectSampleLabel: nonEmptyString(entry?.subjectSampleLabel),
    ownSampleLabel: nonEmptyString(entry?.ownSampleLabel),
  } satisfies MultiSubjectCopy
  return Object.values(copy).some(Boolean) ? copy : null
}

/** Never mixes languages within the sentence: all fields come from the page entry, else from English. */
function multiSubjectCopy(
  entry: ConsentToTestingEntry | undefined,
  english: ConsentToTestingEntry | undefined,
  locale: string
): Pick<ConsentLanguage, keyof MultiSubjectCopy | 'multiSubjectLocale'> {
  const own = multiSubjectCopyOf(entry)
  if (own) return { ...own, multiSubjectLocale: localeForClientConfigLanguage(entry?.language) ?? locale }
  const fallback = multiSubjectCopyOf(english)
  return fallback ? { ...fallback, multiSubjectLocale: 'en' } : {}
}

function extractConsentLanguage(payload: ConsentConfigPayload | undefined, locale: string): ConsentLanguage | null {
  const entry = consentToTestingFromConsentConfig(payload, locale)
  const title = nonEmptyString(entry?.title)
  const paragraphs = nonEmptyStringArray(entry?.descriptionParagraphs)
  if (!title || !paragraphs) return null
  return {
    primaryConsentParagraphs: paragraphs,
    primaryGuardianCheckboxText: title,
    comparatorTitle: nullableNonEmptyString(entry?.comparatorTitle),
    guardianProbandOnlyTitle: nullableNonEmptyString(entry?.guardianProbandOnlyTitle),
    guardianDuoTrioTitle: nullableNonEmptyString(entry?.guardianDuoTrioTitle),
    ...multiSubjectCopy(entry, englishEntry(payload), locale),
  }
}

function classifyHttpError(status: number, code: string | undefined): ConsentLanguageError {
  if (code === MULTIPLE_PENDING_ORDERS) return 'multiple_pending_orders'
  if (code === CONSENT_TO_TESTING_NOT_APPLICABLE) return 'consent_not_applicable'
  if (status === 404) return 'not_found'
  if (status === 502) return 'upstream_error'
  if (status === 503) return 'unavailable'
  return 'upstream_error'
}

export type UseConsentLanguageResult = {
  language: ConsentLanguage | null
  payload: ConsentConfigPayload | null
  loading: boolean
  error: ConsentLanguageError | null
}

export function useConsentLanguage(): UseConsentLanguageResult {
  const apiClient = useFamilyPortalApiClient()
  const intl = useIntl()
  const [payload, setPayload] = useState<ConsentConfigPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState<ConsentLanguageError | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setFetchError(null)

    apiClient
      .get('/config/consents/primary')
      .then(async (res) => {
        if (cancelled) return
        if (!res.ok) {
          const code = await readConsentErrorCode(res)
          if (cancelled) return
          setPayload(null)
          setFetchError(classifyHttpError(res.status, code))
          return
        }
        try {
          const data = (await res.json()) as ConsentConfigPayload
          setPayload(data)
          setFetchError(null)
        } catch {
          setPayload(null)
          setFetchError('invalid_payload')
        }
      })
      .catch(() => {
        if (cancelled) return
        setPayload(null)
        setFetchError('unavailable')
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [apiClient])

  const language = useMemo(() => extractConsentLanguage(payload ?? undefined, intl.locale), [payload, intl.locale])

  const error = useMemo((): ConsentLanguageError | null => {
    if (fetchError) return fetchError
    if (!loading && payload && !language) return 'missing_content'
    return null
  }, [fetchError, loading, payload, language])

  return { language, payload, loading, error }
}
